import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom'

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Link from '@material-ui/core/Link';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListSubheader from '@material-ui/core/ListSubheader';

import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';

import Loading from 'app/components/loaders/Loading';
import useApi from 'app/context/useApi';

import VaporExtension from 'app/utils/VaporExtension';
import FileExtensions from 'app/utils/FileExtensions';

const steps = ['Formatting Rules', 'File Import', 'Import Status'];

const useStyles = makeStyles(theme => ({
    input: {
        display: 'none',
    },
    listSubHeader: {
        backgroundColor: '#ffffff',
        color: theme.palette.error.main
    },
    marginTop: {
        marginTop: theme.spacing(3)
    }
}));

export default function FormImportDialog(props) {

    const classes = useStyles();
    const api = useApi();

    const { open, onClose, formID } = props;

    const [ activeStep, setActiveStep ] = useState(0);

    const [ file, setFile ] = useState(null);

    const [ importErrors, setImportErrors ] = useState([]);
    const [ importLoading, setImportLoading ] = useState(false);

    const handleImportClose = () => {
        setActiveStep(0);
        setFile(null);
        setImportErrors([]);
        onClose();
    }

    const handleNext = () => {
        const nextStep = activeStep + 1;
        if (nextStep === 2) importForm();
        else if (nextStep === 3) handleImportClose();
        else setActiveStep(nextStep);
    }

    const handleBack = () => {
        const previousStep = activeStep - 1;
        if (previousStep === -1) handleImportClose();
        else setActiveStep(previousStep);
    }

    const handleFile = () => {
        const fileInput = document.getElementById('import-input');
        setFile(fileInput.files[0]);
    }

    const getButtonText = () => {
        const step = activeStep
        if (step === 2) return 'Finish';
        if (step === 1) return 'Import';
        return 'Next';
    }

    const importForm = () => {
        if (file) {

            setImportLoading(true);
            const token = api.defaults.headers.common['Authorization']

            VaporExtension.store(file, {}, token).then(response => {
                api.post('/admin/import/entries', {
                    uuid: response.uuid,
                    url: response.url,
                    key: response.key,
                    extension: FileExtensions[file.type],
                    bucket: response.bucket,
                    form_id: formID
                }).then(res => {
                    setActiveStep(2);
                    setImportErrors(res);
                }).finally(() => setImportLoading(false));
            });
        }
    }

    const hasImportErrors = () => {
        if (importErrors && importErrors.errors) return true;
        return false;
    }

    const hasImportFailures = () => {
        if (importErrors && importErrors.failures) return true;
        return false;
    }

    return (
        <Dialog
            open={open}
            onClose={handleImportClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="import-form-title"
            >
            <Loading isLoading={importLoading}/>
            <DialogTitle id="import-form-title">
                Import Form
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((label, index) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                            {(index == 0) &&
                                <StepContent>
                                    <Typography>
                                        Be sure your file is properly formatted. The first row will be used to match the field name to the value. It should match the name given on the form. If importing users, please use their ID from the user table.
                                    </Typography>
                                </StepContent>
                            }
                            {(index == 1) &&
                                <StepContent>
                                    <Grid container alignItems="center" spacing={2}>
                                        <Grid item>
                                            <input
                                                accept="*/*"
                                                className={classes.input}
                                                id="import-input"
                                                type="file"
                                                onChange={handleFile}
                                            />
                                            <label htmlFor="import-input">
                                                <Button component="span">
                                                    Open
                                                </Button>
                                            </label>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2" color="primary">
                                                { file ? file.name : 'none'}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </StepContent>
                            }
                            {(index == 2) &&
                                <StepContent>
                                    { hasImportErrors() ? (
                                        <List
                                            subheader={
                                                <ListSubheader className={classes.listSubHeader}>
                                                    Database Errors
                                                </ListSubheader>
                                            }
                                            >
                                            {importErrors.errors.map((error, index) => (
                                                <ListItem button key={index}>
                                                    <ListItemText primary={error} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            No Import Errors.
                                        </Typography>
                                    )}
                                    { hasImportFailures() ? (
                                        <List
                                            subheader={
                                                <ListSubheader className={classes.listSubHeader}>
                                                    Validation Failures
                                                </ListSubheader>
                                            }
                                            >
                                            {importErrors.failures.map((failure, index) => (
                                                <ListItem button key={index}>
                                                    <ListItemText primary={failure.message} secondary={"Row: " + failure.row} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            No Import Failures.
                                        </Typography>
                                    )}
                                </StepContent>
                            }
                        </Step>
                    ))}
                </Stepper>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleBack}>
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>
                <Button
                    onClick={handleNext}
                    variant="contained"
                    color={activeStep === 3 ? 'default' : 'primary'}
                    >
                    {getButtonText()}
                </Button>
           </DialogActions>
        </Dialog>
    )
}